// src/shared/changelog/seenStore.ts

/**
 * localStorage key holding the `entryKey`s of the changelog entries the user has
 * already read (`string[]`).
 *
 * Replaces the retired `lastSeenId` key, which held a single value and therefore
 * could only answer "has *a* release been announced". The question the modal
 * actually asks has one answer per entry — "did you read *this* one" — and a
 * single flag cannot express "the user saw 1 of 3" (CTX-500 §2.3).
 *
 * The old key is deliberately **not** migrated and **not** read: its value is an
 * `entryKey` the user did see, but carrying it over would say nothing about the
 * other entries of the same month, which is the very gap this replaces. The
 * honest outcome is that the card tints once after the upgrade.
 *
 * Plain localStorage, like `clearedNewItems` (CTX-511), so the store's persist
 * whitelist and rehydration stay untouched — this is per-device UI state.
 */
export const SEEN_ENTRIES_KEY = "ctx:changelog:seenEntries";

/**
 * localStorage key recording that this device's seen set was **seeded** rather
 * than read (§2.8).
 *
 * It exists to tell those two apart. A first-time user gets every entry of the
 * visible feed dropped into the set so they are not handed a walkthrough of
 * changes to a product they have not met — but "we assumed you did not need
 * this" is not the same fact as "you read it", and only this key preserves the
 * difference if the state ever moves to the server.
 */
export const BOOTSTRAPPED_AT_KEY = "ctx:changelog:bootstrappedAt";

/**
 * Cross-tab + same-tab notification that the seen set changed.
 *
 * The name is kept from the `lastSeenId` era on purpose: it is an event name, no
 * value is attached to it, and every listener re-reads the store anyway.
 */
export const SEEN_EVENT = "ctx:changelog:seen";

/**
 * The seam every surface talks to instead of `localStorage` (CTX-500 §2.4).
 *
 * v1 is per-device and that is assumed explicitly — three browsers give the
 * indicator three times. The interface is what keeps the decision cheap to
 * revisit: moving the state to the server (§5 of `CHANGELOG_MODAL_ENDPOINTS.md`)
 * becomes a new file implementing this, not a refactor of the consumers. Which
 * is also why the methods are async although the localStorage one never awaits
 * anything.
 *
 * `add` takes a **list** rather than the single key the decision doc sketched:
 * §2.2 marks several entries at once in three of its four rows (`Done` marks the
 * whole walkthrough, `✕` marks everything that was shown, `/changelog` marks the
 * feed), and a loop of single-key calls would be N read-modify-writes and N
 * events for one user action.
 */
export interface SeenStore {
  read(): Promise<string[]>;
  add(entryKeys: string[]): Promise<void>;
  /**
   * Seeds a device with no history and records that it happened.
   *
   * Writing `BOOTSTRAPPED_AT_KEY` is part of the same call rather than the
   * caller's job: the seed and the note that it was a seed are one fact, and
   * two callers remembering to write both is how they drift.
   */
  bootstrap(keys: string[]): Promise<void>;
  /** Whether this device has been seeded — the guard that keeps it to once. */
  hasBootstrapped(): Promise<boolean>;
}

/**
 * Reads the set, treating anything unexpected as "nothing seen yet".
 *
 * That default is the opposite of `useChangelogNewItems`'s, and deliberately so.
 * There, failing open means tagging every row of the rail; here it means the
 * card stays tinted and the modal announces a release again — the same thing
 * that happens to anyone on a new device, which is a state the feature already
 * handles. Failing the other way would silence an announcement nobody read.
 */
function readSeenKeys(): Set<string> {
  try {
    const raw = window.localStorage.getItem(SEEN_ENTRIES_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((key): key is string => typeof key === "string"));
  } catch {
    return new Set();
  }
}

function writeSeenKeys(keys: Set<string>): void {
  try {
    window.localStorage.setItem(SEEN_ENTRIES_KEY, JSON.stringify([...keys]));
  } catch {
    // Ignore — a full or blocked storage just means the tint returns next load.
  }
}

/**
 * Same-tab listeners get nothing from `storage`, which only fires in the *other*
 * tabs, so the event is dispatched explicitly — the pattern `clearedNewItems`
 * already uses.
 */
function notifySeenChanged(): void {
  window.dispatchEvent(new Event(SEEN_EVENT));
}

export const localStorageSeenStore: SeenStore = {
  async read() {
    return [...readSeenKeys()];
  },

  /**
   * Re-reads before merging rather than trusting a set held in React state:
   * another tab may have added keys since this one rendered, and a write built
   * on a stale snapshot would drop them.
   *
   * A call that adds nothing new writes nothing and announces nothing. That is
   * not just an optimisation — `ChangelogPage` marks its whole feed from an
   * effect, and an event on every such call would re-render it into a loop.
   */
  async add(entryKeys) {
    if (entryKeys.length === 0) return;
    const next = readSeenKeys();
    const sizeBefore = next.size;
    for (const key of entryKeys) next.add(key);
    if (next.size === sizeBefore) return;
    writeSeenKeys(next);
    notifySeenChanged();
  },

  /**
   * Seeds a device that has no history, so a first-time user is not handed a
   * walkthrough of changes to a product they have not met yet (§2.8).
   *
   * It **replaces** rather than merges: "this device starts here" is a different
   * statement from "the user read these", and the caller — CTX-592, which owns
   * §2.8 along with the `ctx:changelog:bootstrappedAt` key that records it
   * happened — only makes it when there is nothing to lose.
   */
  async bootstrap(keys) {
    writeSeenKeys(new Set(keys));
    try {
      window.localStorage.setItem(BOOTSTRAPPED_AT_KEY, new Date().toISOString());
    } catch {
      // Ignore — the seed itself is what matters, and a device that cannot
      // record the note simply gets seeded again next session. Harmless: the
      // second seed writes the same keys.
    }
    notifySeenChanged();
  },

  async hasBootstrapped() {
    try {
      return window.localStorage.getItem(BOOTSTRAPPED_AT_KEY) !== null;
    } catch {
      // Unreadable storage reads as "already seeded" so an unusable device is
      // never handed the first-session walkthrough on every navigation.
      return true;
    }
  },
};
